import { useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  AddTeamMembersToTeamDocument,
  CreateTeamDocument,
  type CreateTeamInput,
  DeleteTeamDocument,
  RemoveTeamMembersFromTeamDocument,
  UpdateTeamDocument,
  type UpdateTeamInput,
  UpdateTeamMembershipDocument,
  ValidateTeamKeyDocument,
  useAppData,
  type TeamMembershipRole,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * `silent`: the caller owns the feedback. The hook announces nothing — no
 * error toast — and a failure (a non-success union member
 * or a thrown request) REJECTS instead of resolving null, so a bulk runner
 * that fans one call out per member can report once for the whole batch
 * (`settleAll` + the bulk bar's own toasts) rather than once per member.
 */
export type MutationFeedbackOptions = { silent?: boolean };

/** A non-success union member (ValidationError, PermissionDenied…) carrying
 *  its server message, so the catch path can tell it from a request error. */
class ResultError extends Error {}
// createTeam / deleteTeam are gated by a server-side @RequireAccess decorator
// that THROWS a FORBIDDEN_* error (rather than returning a PermissionDenied
// union), so a denial lands in the catch. The global error link in
// apollo-client.ts already toasts those, so the catch blocks only toast the
// generic failure for NON-forbidden errors. updateTeam and the membership
// mutations return PermissionDenied as a union variant instead — the typed
// `"code" in result` branches handle those.
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";
import { createEditSessionGate } from "#app/lib/analytics/edit-session";


// Autosaved settings fields write once per typing pause; one session of
// edits to one field counts once.
const autosaveSession = createEditSessionGate(60_000);
export function useTeamMutations() {
  const client = useApolloClient();
  const { track } = useAnalytics();
  // The workspace team list (`useAppData().teams` → the sidebar rows, the
  // masthead crumb, the entity maps) is held as state, not read live from the
  // cache, so an identity write has to refresh it explicitly.
  const { refetch: refetchAppData } = useAppData();
  const [createTeamMutation] = useMutation(CreateTeamDocument);
  const [updateTeamMutation] = useMutation(UpdateTeamDocument);
  const [deleteTeamMutation] = useMutation(DeleteTeamDocument);
  const [addTeamMembersMutation] = useMutation(AddTeamMembersToTeamDocument);
  const [removeTeamMembersMutation] = useMutation(RemoveTeamMembersFromTeamDocument);
  const [updateTeamMembershipMutation] = useMutation(UpdateTeamMembershipDocument);

  const createTeam = useCallback(
    async (input: CreateTeamInput, via = "unknown") => {
      try {
        const { data } = await createTeamMutation({
          variables: { input },
          refetchQueries: ["TeamsListData"],
        });
        const result = data?.createTeam;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to create team", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "team", data: { via } });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to create team");
        return null;
      }
    },
    [createTeamMutation, track]
  );

  const updateTeam = useCallback(
    // `autosave` marks a keystroke-debounced auto-save (the team-edit text
    // fields) so analytics count the typing session once; no success toast
    // either way, errors still toast. (`silent` is the bulk contract above.)
    async (
      teamId: string,
      input: UpdateTeamInput,
      options?: {
        autosave?: boolean;
        /** The surface, for analytics: "team_menu", "settings". */
        via?: string;
        /** A refusal goes here instead of a toast — for a field that shows
         *  the server's reason under itself. */
        onError?: (message: string) => void;
      },
    ) => {
      try {
        const { data } = await updateTeamMutation({
          variables: { teamId, input },
        });
        const result = data?.updateTeam;
        if (result && "code" in result) {
          const message = result.message ?? "Failed to update team";
          if (options?.onError) options.onError(message);
          else toastManager.add({ title: message, type: "error" });
          return null;
        }
        const fields = Object.keys(input).sort();
        // An autosaved text field writes per typing pause: count its session.
        if (!options?.autosave || autosaveSession(`${teamId}:${fields.join(",")}`)) {
          track({
            context: AnalyticsContext.Settings,
            event: SettingsEvent.Update,
            label: "team",
            data: { fields, via: options?.via ?? "unknown" },
          });
        }
        void refetchAppData();
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to update team");
        return null;
      }
    },
    [updateTeamMutation, track, refetchAppData]
  );

  const deleteTeam = useCallback(
    async (teamId: string) => {
      try {
        const { data } = await deleteTeamMutation({
          variables: { teamId },
          refetchQueries: ["TeamsListData"],
        });
        const result = data?.deleteTeam;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to delete team", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "team" });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to delete team");
        return null;
      }
    },
    [deleteTeamMutation, track]
  );

  const addTeamMembers = useCallback(
    async (
      teamId: string,
      userIds: string[],
      role: TeamMembershipRole = "member",
      via = "unknown",
      options?: MutationFeedbackOptions,
    ) => {
      try {
        const { data } = await addTeamMembersMutation({
          variables: { teamId, userIds, role },
          // The returned team updates the detail's leads/members in the
          // cache, but the roster's paged `teamMembers` connection is a
          // separate list: refetch it so an add from any mount of the action
          // (team masthead, settings header) lands in whichever roster is
          // showing.
          refetchQueries: ["GetTeamMembers"],
        });
        const result = data?.addTeamMembers;
        if (result && "code" in result) {
          throw new ResultError(result.message ?? "Failed to add members");
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.AddMembers, label: "team", data: { count: userIds.length, role, via } });
        return result ?? null;
      } catch (e) {
        if (options?.silent) throw e;
        if (e instanceof ResultError) toastManager.add({ title: e.message, type: "error" });
        else toastMutationError(e, "Failed to add members");
        return null;
      }
    },
    [addTeamMembersMutation, track]
  );

  const removeTeamMembers = useCallback(
    async (teamId: string, userIds: string[], options?: MutationFeedbackOptions) => {
      try {
        const { data } = await removeTeamMembersMutation({
          variables: { teamId, userIds },
        });
        const result = data?.removeTeamMembers;
        if (result && "code" in result) {
          throw new ResultError(result.message ?? "Failed to remove members");
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.RemoveMembers, label: "team", data: { count: userIds.length } });
        return result ?? null;
      } catch (e) {
        if (options?.silent) throw e;
        if (e instanceof ResultError) toastManager.add({ title: e.message, type: "error" });
        else toastMutationError(e, "Failed to remove members");
        return null;
      }
    },
    [removeTeamMembersMutation, track]
  );

  /** In-place role change — one UPDATE on the membership row, never the
   *  remove-then-add pair that could strand a user off the team. */
  const setTeamMemberRole = useCallback(
    async (
      teamId: string,
      userId: string,
      role: TeamMembershipRole,
      options?: MutationFeedbackOptions,
    ) => {
      try {
        const { data } = await updateTeamMembershipMutation({
          variables: { teamId, userId, role },
        });
        const result = data?.updateTeamMembership;
        if (result && "code" in result) {
          throw new ResultError(result.message ?? "Failed to change role");
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.UpdateMembership, label: "team", data: { role } });
        return result ?? null;
      } catch (e) {
        if (options?.silent) throw e;
        if (e instanceof ResultError) toastManager.add({ title: e.message, type: "error" });
        else toastMutationError(e, "Failed to change role");
        return null;
      }
    },
    [updateTeamMembershipMutation, track]
  );

  /** Smart diff — only adds new members and removes deleted ones. */
  const updateTeamMembership = useCallback(
    async (
      teamId: string,
      currentUserIds: string[],
      nextUserIds: string[],
      role: TeamMembershipRole = "member"
    ) => {
      try {
        const currentSet = new Set(currentUserIds);
        const nextSet = new Set(nextUserIds);

        const toAdd = nextUserIds.filter((id) => !currentSet.has(id));
        const toRemove = currentUserIds.filter((id) => !nextSet.has(id));

        await Promise.all([
          ...(toAdd.length > 0
            ? [addTeamMembersMutation({ variables: { teamId, userIds: toAdd, role } })]
            : []),
          ...(toRemove.length > 0
            ? [removeTeamMembersMutation({ variables: { teamId, userIds: toRemove } })]
            : []),
        ]);
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.UpdateMembership, label: "team", data: { added: toAdd.length, removed: toRemove.length } });
      } catch (e) {
        toastMutationError(e, "Failed to update team membership");
      }
    },
    [addTeamMembersMutation, removeTeamMembersMutation, track]
  );

  /** True when the key is available (mirrors useWorkflowMutations). */
  const validateTeamKey = useCallback(
    async (key: string) => {
      const { data, error } = await client.query({
        query: ValidateTeamKeyDocument,
        variables: { key },
        fetchPolicy: "network-only",
      });
      // errorPolicy "all" resolves with `error` set instead of rejecting —
      // rethrow so the caller's catch path stays the failure path.
      if (!data) throw error ?? new Error("Failed to validate team key");
      return data.validateTeamKey;
    },
    [client],
  );

  return {
    createTeam,
    updateTeam,
    deleteTeam,
    addTeamMembers,
    removeTeamMembers,
    setTeamMemberRole,
    updateTeamMembership,
    validateTeamKey,
  };
}
